import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router';
import formatISO from 'date-fns/formatISO';
import { useSelector } from 'react-redux';
import { getMenuItems, MenuItemModel } from 'store/slices/menuSlice';
import { DictionaryDto, useGetApi } from 'api/dictionary';
import { fetchAccessedStatuses, StatusCommonDto } from 'api/statuses';
import { UserListDto } from 'api/user';
import { CourierDto, fetchActiveCouriers, fetchCouriersByOrderCount } from 'api/couriers';
import { fetchRegionsDict } from 'api/regions';
import { fetchEmcTypes } from 'api/emc';
import { getUserBranches } from 'api/branches';
import {
    fetchActions,
    fetchOrderTasks,
    getPaged,
    OrderAction,
    OrderDto,
    OrderParams,
    postTransitionTask,
    updateStatus,
} from 'api/orders';
import { getUserInfo } from '../../store/slices/userInfoSlice';
import { fetchGeozoneList, GeocodingStatusesEnum } from 'api/relog';
import { DROPDOWN_SELECT_ALL, GEOZONED, NOT_GEOCODED, NOT_GEOZONE } from './types';

interface Params {
    status: string | undefined;
}

export function useOrderLogic() {
    const { enqueueSnackbar } = useSnackbar();
    const { roles, branch } = useSelector(getUserInfo);
    const relogEnabled = branch?.relog_enabled;
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(true);
    const [items, setItems] = useState<OrderDto[]>([]);
    const [menuStatusId, setMenuStatusId] = useState<string | undefined>(undefined);
    const [totalSum, setTotalSum] = useState<number>(0);
    const params = useParams<Params>();
    const allMenu = useSelector(getMenuItems);
    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(100);
    const [total, setTotal] = useState(0);
    const [selected, setSelected] = React.useState<number[]>([]);
    const [singleSelect, setSingleSelect] = useState<boolean>(false);
    // Courier pagination
    const [courierPage, setCourierPage] = useState(0);
    const [courierCount, setCourierCount] = useState(0);
    const [courierPerPage] = useState(25);

    // filters
    const [productId, setProductId] = useState<number | string>('');
    const [productCode, setProductCode] = useState<string | null>('');
    const [emcTypeId, setEmcTypeId] = useState<number | string>('');
    const [products, setProducts] = useState<DictionaryDto[]>([]);
    const [emcTypes, setEmcTypes] = useState<DictionaryDto[]>([]);
    const [statusId, setStatusId] = useState<number | string>('');
    const [statuses, setStatuses] = useState<StatusCommonDto[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [untilDate, setUntilDate] = useState<Date | null>(null);
    const [search, setSearch] = useState<string>('');
    const [regionId, setRegionId] = useState<number | string>('');
    const [regionCode, setRegionCode] = useState<string | null>('');
    const [regions, setRegions] = useState<DictionaryDto[]>([]);
    const [branchId, setBranchId] = useState<number | string>('');
    const [branchCode, setBranchCode] = useState<string | null>('');
    const [branches, setBranches] = useState<DictionaryDto[] | null>([]);
    const [actions, setActions] = useState<OrderAction[]>([]);
    const [commentOpen, setCommentOpen] = useState<boolean>(false);
    const [activeCouriers, setActiveCouriers] = useState<(UserListDto | CourierDto)[]>([]);
    const courier = useRef<UserListDto | CourierDto | null>(null);
    const { fetchAll: fetchProducts } = useGetApi<DictionaryDto>('/product/by-current-role');
    const [activeAction, setActiveAction] = useState('');
    const [courierDialogOpen, setCourierDialogOpen] = useState(false);
    const [assignCourierTaskId, setAssignCourierTaskId] = useState(0);
    const [selectedItems, setSelectedItems] = useState<OrderDto[]>([]);
    const [selectedOpen, setSelectedOpen] = useState(false);
    const [orderDetailOpen, setOrderDetailOpen] = useState(false);
    const [orderId, setOrderId] = useState<number>(0);
    const [barCodeLoading, setBarCodeLoading] = useState(false);
    const [courierSelectLoading, setCourierSelectLoading] = useState(false);
    const [tabValue, setTabValue] = React.useState(0);
    const [courierList, setCourierList] = useState<CourierDto[]>([]);
    const [courierHasMore, setCourierHasMore] = useState(false);
    const [courierCallOpen, setCourierCallOpen] = useState(false);
    const [geocodingStatuses] = useState<{ id: string, description: string }[]>([
        { id: GeocodingStatusesEnum.ALL, description: DROPDOWN_SELECT_ALL },
        { id: GeocodingStatusesEnum.GEOCODED, description: 'Геокодированные' },
        { id: GeocodingStatusesEnum.NOT_GEOCODED, description: 'Не геокодированные' },
    ]);
    const [geocodingStatusId, setGeocodingStatusId] = useState<GeocodingStatusesEnum>(GeocodingStatusesEnum.ALL);

    const [isOrderChanged, setIsOrderChanged] = useState(false);

    const [geozones, setGeozones] = useState<{ id: string, description: string }[]>([{id: DROPDOWN_SELECT_ALL, description: DROPDOWN_SELECT_ALL}]);
    const [geozoneName, setGeozoneName] = useState<string>(DROPDOWN_SELECT_ALL);

    const isCourierCall = useMemo(() => products.find((p) => p.id === productId)?.code === 'CC', [
        productId,
        products,
    ]);

    const getStatusValue = useCallback((): MenuItemModel | undefined => {
        const request = allMenu.find((i) => i.name === 'OrdersByStatuses');
        if (request?.items && request.items.length === 0) {
            return undefined;
        }

        return request?.items?.find((i) => i.id === Number(menuStatusId));
    }, [allMenu, menuStatusId]);

    const getDoubleSelect = useCallback((): boolean => {
        const status = getStatusValue();
        return !status?.doubleSelect;
    }, [getStatusValue]);

    const menuStatus = getStatusValue();

    // @ts-ignore
    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
    };

    const handleStatusChange = (e: any) => {
        setStatusId(e.target.value as string);
    };

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
    };

    const handleUntilDateChange = (date: Date | null) => {
        setUntilDate(date);
    };


    const getGeozoneList = async () => {
        try {
            let geozoneList: string[] = relogEnabled ? await fetchGeozoneList(branch?.relog_api_key || '') || [] : [];
            setGeozones([
                { id: DROPDOWN_SELECT_ALL, description: DROPDOWN_SELECT_ALL },
                { id: NOT_GEOCODED, description: NOT_GEOCODED },
                { id: NOT_GEOZONE, description: NOT_GEOZONE },
                { id: 'GEOZONED', description: GEOZONED },
                ...geozoneList.map((g) => ({ id: g, description: g }))
            ]);
        } catch (error) {
            enqueueSnackbar(`Ошибка при получении данных из Relog: ${error}`, {
                variant: 'error',
            });
        }
    };

    // const fillGeozones = async (orderParams: OrderParams) => {
    //     let geozoneList: string[] = relogEnabled ? await getGeozoneList(orderParams) || [] : [];
    //     geozoneList = [DROPDOWN_SELECT_ALL, ...geozoneList];
    //     setGeozones(geozoneList.map((g) => ({ id: g, description: g })));
    // }

    const isReserved = useMemo(() => menuStatus?.code === 'RESERVED', [menuStatus]);

    const isReceived = useMemo(() => menuStatus?.code === 'RECEIVED', [menuStatus]);
    const isGeocoding = useMemo(() => menuStatus?.code === 'GEOCODING', [menuStatus]);
    const isGeozoning = useMemo(() => menuStatus?.code === 'GEOZONING', [menuStatus]);

    const handleSearchChange = (e: any) => {
        setSearch(e.target.value);
    };

    const handleEmcTypeChange = (e: any) => {
        setEmcTypeId(e.target.value as string);
    };

    const handleClick = useCallback(
        (item: OrderDto) => {
            const selectedIndex = selected.indexOf(item.id);
            let newSelected: number[] = [];
            let newSelectedItems: OrderDto[] = [];
            let newSum: number = 0;

            if (singleSelect) {
                if (selectedIndex === -1) {
                    newSelected.push(item.id);
                    newSelectedItems.push(item);
                    if (
                        item?.order_detail?.delivery_type === 'PENSION' &&
                        typeof item?.order_detail?.issue_sum === 'number'
                    ) {
                        newSum = newSum + item?.order_detail?.issue_sum;
                    }
                }
            } else {
                if (selectedIndex === -1) {
                    newSelected = newSelected.concat(selected, item.id);
                    newSelectedItems = [item, ...newSelectedItems.concat(selectedItems)];
                    if (
                        item?.order_detail?.delivery_type === 'PENSION' &&
                        typeof item?.order_detail?.issue_sum === 'number'
                    ) {
                        newSum = totalSum + item?.order_detail?.issue_sum;
                    }
                } else {
                    newSelected = selected.filter((s) => s !== item.id);
                    newSelectedItems = selectedItems.filter((s) => s.id !== item.id);
                    if (
                        item?.order_detail?.delivery_type === 'PENSION' &&
                        typeof item?.order_detail?.issue_sum === 'number'
                    ) {
                        newSum = totalSum - item?.order_detail?.issue_sum;
                    }
                }
            }

            setSelected(newSelected);
            setSelectedItems(newSelectedItems);
            setTotalSum(newSum);
        },
        [selected, selectedItems, singleSelect, totalSum]
    );

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && !singleSelect) {
            const newSelecteds = items.map((n) => n.id);
            const product = products.find((item) => item.id === productId);
            setSelected(newSelecteds);
            setSelectedItems(items);
            if (product?.code === 'KP') {
                const newSum = items.reduce((acc, value) => {
                    const { order_detail } = value;
                    const sum = order_detail?.issue_sum ? order_detail?.issue_sum : 0;
                    return acc + sum;
                }, 0);
                setTotalSum(newSum);
            }

            return;
        }

        setSelected([]);
        setSelectedItems([]);
        setTotalSum(0);
    };

    const handleReset = () => {
        if (emcTypeId) {
            setEmcTypeId('');
        }

        if (!menuStatusId && statusId) {
            setStatusId('');
        }

        if (startDate) {
            setStartDate(null);
        }

        if (untilDate) {
            setUntilDate(null);
        }

        if (search) {
            setSearch('');
        }

        if (regionId) {
            setRegionId('');
        }

        if (courier.current) {
            courier.current = null;
        }
    };

    // Fetch
    const getAll = useCallback(async () => {
        if (isReserved) {
            if (!regionId && !courier.current?.id) {
                return;
            }
        }

        setLoading(true);
        try {
            const order_by_parameter =
                menuStatus?.code === 'IN_PROGRESS' || menuStatus?.code === 'ASSIGNED_COURIER'
                    ? 'date_assigned:asc'
                    : '';
            const orderParams: OrderParams = {
                courier_id: courier.current?.id,
                date_from: startDate ? formatISO(startDate, { representation: 'date' }) : null,
                date_until: untilDate ? formatISO(untilDate, { representation: 'date' }) : null,
                page: page,
                size: perPage,
                product_id: productId,
                region_id: courier.current?.id ? '' : regionId,
                status_id: statusId,
                mail_type_id: emcTypeId,
                value: isReserved ? '' : search,
                branch_id: branchId,
                order_by_parameter,
                geocoding_status_code: (geocodingStatusId === GeocodingStatusesEnum.ALL ? null : geocodingStatusId) as GeocodingStatusesEnum,
            }
            const data = await getPaged({
                ...orderParams,
                geozone_name: [DROPDOWN_SELECT_ALL, NOT_GEOCODED].includes(geozoneName) ? null : geozoneName,
                geocoding_status_code: (geozoneName === NOT_GEOCODED ? 'NOT_GEOCODED' : null) as GeocodingStatusesEnum,
            });

            // await fillGeozones(orderParams);
            await getGeozoneList();

            setItems(data.content.map((i) => ({ ...i, isGeozoneFined: i.geozone_name === NOT_GEOZONE ? false : true })));
            setTotal(data.total_elements);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [
        menuStatus,
        isReserved,
        regionId,
        startDate,
        untilDate,
        page,
        perPage,
        productId,
        statusId,
        emcTypeId,
        search,
        branchId,
        enqueueSnackbar,
        geocodingStatusId,
        geozoneName
    ]);

    const getBranchesByRegionCode = useCallback(
        async (regionCode: string | null, productCode: string | null) => {
            try {
                const branchList = await getUserBranches(
                    regionCode as string,
                    productCode as string
                );
                if (branchList?.length) {
                    setBranches(
                        branchList.map(
                            (b) =>
                                ({ id: b.id, description: b.title, code: b.code } as DictionaryDto)
                        )
                    );
                } else {
                    setBranches(null);
                }

                if (branchList.length === 1) {
                    setBranchId(branchList[0].id as number);
                    setBranchCode(branchList[0].code);
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar]
    );

    const loadBranches = useCallback(
        async (regionId: number | string, productCode: string | null) => {
            const regionCode = regions.find((r) => r.id === regionId)?.code;
            if (!regionCode) {
                return;
            }

            await getBranchesByRegionCode(regionCode, productCode);
        },
        [getBranchesByRegionCode, regions, products]
    );

    const handleCourierCallClose = useCallback(
        (reload?: boolean) => {
            if (reload) {
                getAll();
            }

            setCourierCallOpen(false);
        },
        [getAll]
    );

    const handleCourierCallClick = () => {
        setCourierCallOpen(true);
    };

    // @ts-ignore
    const loadCouriers = useCallback(
        async (force = false) => {
            if (!regionId) {
                return;
            }

            setCourierHasMore(true);

            try {
                const list = await fetchCouriersByOrderCount({
                    page: force ? 0 : courierPage,
                    size: courierPerPage,
                    region_id: regionId,
                    order_status_id: statusId,
                    product_id: productId,
                    value: search,
                });
                setCourierList([...(force ? [] : courierList), ...list.content]);
                setCourierCount(list.total_elements);

                if (list.content?.length && force) {
                    courier.current = list.content[0];
                    if (page === 0) {
                        getAll();
                    } else {
                        setPage(0);
                    }

                    setCourierPage(0);
                }

                if (list.total_elements > courierPerPage * (courierPage + 1)) {
                    setCourierPage(courierPage + 1);
                    setCourierHasMore(true);
                } else {
                    setCourierHasMore(false);
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            }
        },
        [
            regionId,
            courierPage,
            courierPerPage,
            statusId,
            productId,
            search,
            courierList,
            page,
            getAll,
            enqueueSnackbar,
            geocodingStatusId,
            geozoneName
        ]
    );

    const handleReserveSearch = useCallback(async () => {
        if (isReserved) {
            setTabValue(0);
            courier.current = null;
            setCourierPage(0);
            setCourierList([]);
            setPage(0);
            setItems([]);
            await loadCouriers(true);
        }
    }, [isReserved, loadCouriers]);

    // @ts-ignore
    const handleSearch = useCallback(async () => {
        if (isReserved) {
            await handleReserveSearch();
        } else {
            await getAll();
        }
    }, [getAll, handleReserveSearch, isReserved]);

    const handleRegionChange = useCallback(
        async (e: any) => {
            const value = e.target.value as string;
            courier.current = null;
            setBranchId('');

            if (!isReserved && value) {
                const code = regions.find((r) => r.id === value)?.code || null;
                const res = await fetchActiveCouriers(code || null);
                setRegionCode(code);
                setActiveCouriers(res.content);
                setRegionId(value);
            } else {
                setActiveCouriers([]);
                setCourierPage(0);
                setCourierList([]);
                if (!value) {
                    setCourierList([]);
                }

                setRegionId(value);
            }

            await loadBranches(value, productCode || '');

            setSelected([]);
            setSelectedItems([]);
            setTotalSum(0);
        },
        [isReserved, loadBranches, regions, productCode]
    );

    const handleProductChange = (e: any) => {
        const value = e.target.value as string;
        setProductId(value);
        const code = products.find((r) => r.id === value)?.code || null;
        setProductCode(code);
        getBranchesByRegionCode(regionCode || null, code || '');
    };

    const handleGeocodingStatusChange = (e: any) => {
        const value = e.target.value as GeocodingStatusesEnum;
        setSelected([]);
        setGeocodingStatusId(value);
    };

    const handleGeozoneChange = (e: any) => {
        const value = e.target.value as string;
        setSelected([]);
        setGeozoneName(value);
    };

    const handleBranchChange = useCallback(
        (e: any) => {
            const value = e.target.value as string;
            const code = branches?.find((r) => r.id === value)?.code || null;
            setBranchId(value);
            setBranchCode(code);
        },
        [branches]
    );

    const getAllWithBarcode = useCallback(
        async (val: string) => {
            if (!val) {
                return;
            }

            let res = true;
            setBarCodeLoading(true);
            try {
                const order_by_parameter =
                    menuStatus?.code === 'IN_PROGRESS' || menuStatus?.code === 'ASSIGNED_COURIER'
                        ? 'date_assigned:asc'
                        : '';
                const orderParams: OrderParams = {
                    courier_id: courier.current?.id,
                    date_from: startDate ? formatISO(startDate, { representation: 'date' }) : null,
                    date_until: untilDate ? formatISO(untilDate, { representation: 'date' }) : null,
                    page: 0,
                    size: perPage,
                    product_id: productId,
                    region_id: courier.current?.id ? '' : regionId,
                    status_id: statusId,
                    mail_type_id: emcTypeId,
                    order_number: val,
                    order_by_parameter,
                    geocoding_status_code: (geocodingStatusId === GeocodingStatusesEnum.ALL ? null : geocodingStatusId) as GeocodingStatusesEnum,
                }

                const data = await getPaged({ ...orderParams, geozone_name: geozoneName === DROPDOWN_SELECT_ALL ? null : geozoneName });

                // await fillGeozones(orderParams);
                await getGeozoneList();

                if (data.content.length === 0) {
                    enqueueSnackbar('Заявка не найдена', {
                        variant: 'warning',
                    });
                } else {
                    handleClick(data.content[0]);
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
                res = false;
            } finally {
                setBarCodeLoading(false);
            }

            return new Promise<boolean>((resolve) => {
                resolve(res);
            });
        },
        [
            menuStatus,
            courier,
            emcTypeId,
            enqueueSnackbar,
            handleClick,
            perPage,
            productId,
            regionId,
            startDate,
            statusId,
            untilDate,
            geocodingStatusId,
            geozoneName
        ]
    );

    const handleUpdateStatus = useCallback(
        async (code: string, requireModal: boolean) => {
            try {
                if (requireModal) {
                    setLoading(false);
                    setActiveAction(code);
                    setCommentOpen(true);
                } else {
                    setLoading(true);
                    await updateStatus({
                        status: code,
                        comment: null,
                        id: selected.length === 1 ? selected[0] : null,
                        ids: selected,
                    });
                    enqueueSnackbar('Успешно выполнено', { variant: 'success' });
                    setSelected([]);
                    setSelectedItems([]);
                    setTotalSum(0);
                    if (isReserved) {
                        await handleReserveSearch();
                    } else {
                        await getAll();
                    }
                }
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setLoading(false);
            }
        },
        [enqueueSnackbar, getAll, handleReserveSearch, isReserved, selected]
    );

    const sendTransitionTask = useCallback(
        async (taskId: number, courierId: number | undefined = undefined) => {
            const taskResult = await postTransitionTask(selected[0], taskId, selected, courierId);

            if (!taskResult.success) {
                enqueueSnackbar('Ошибка при отправке данных');
            }
        },
        [enqueueSnackbar, selected]
    );

    const handleActionClick = useCallback(
        async (code: string, requireModal: boolean) => {
            try {
                setActiveAction(code);
                setActionLoading(true);
                const tasks = await fetchOrderTasks(code, selected[0]);

                if (tasks.length > 0) {
                    const orderTaskList = tasks.filter(
                        (t) => t.event_task.code === 'PUS_CREATE_DELIVERY_LIST_SERVICE'
                    );

                    if (orderTaskList.length > 0) {
                        await sendTransitionTask(orderTaskList[0].id);
                    }

                    const assignCourierTaskList = tasks.filter(
                        (t) => t.event_task.code === 'ASSIGN_COURIER_SERVICE'
                    );

                    if (assignCourierTaskList.length > 0) {
                        setAssignCourierTaskId(assignCourierTaskList[0].id);
                        setCourierDialogOpen(true);
                        return;
                    }
                }

                await handleUpdateStatus(code, requireModal);
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setActionLoading(false);
            }
        },
        [enqueueSnackbar, handleUpdateStatus, selected, sendTransitionTask]
    );

    const handleActionCourierSelect = useCallback(
        async (courierId: number | undefined) => {
            try {
                setCourierSelectLoading(true);
                await sendTransitionTask(assignCourierTaskId, courierId);
                await updateStatus({
                    status: activeAction,
                    comment: null,
                    id: selected.length === 1 ? selected[0] : null,
                    ids: selected,
                });
                enqueueSnackbar('Успешно выполнено', {
                    variant: 'success',
                });
                setCourierDialogOpen(false);
                setSelected([]);
                setSelectedItems([]);
                setTotalSum(0);
                await getAll();
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных: ${e.response?.data?.message || e}`, {
                    variant: 'error',
                    autoHideDuration: 10000,
                });
            } finally {
                setCourierSelectLoading(false);
            }
        },
        [activeAction, assignCourierTaskId, enqueueSnackbar, getAll, selected, sendTransitionTask]
    );

    const handleActionCourierClose = () => {
        setCourierDialogOpen(false);
    };

    const handleCommentClose = (success: boolean = false) => {
        if (success) {
            setSelected([]);
            setSelectedItems([]);
            setTotalSum(0);
            getAll();
        }

        setCommentOpen(false);
    };

    const isSelected = (id: number) => selected.indexOf(id) !== -1;

    const getActions = useCallback(() => {
        if (menuStatusId) {
            const status = menuStatus;
            if (!status || !productId) {
                return;
            }

            setActionLoading(true);
            fetchActions(status?.code, productId)
                .then((res) => {
                    setActions(res);
                })
                .finally(() => setActionLoading(false));
        } else {
            setActionLoading(false);
        }
    }, [menuStatus, menuStatusId, productId]);

    const getDictionaries = useCallback(async () => {
        if (products.length !== 0) {
            return;
        }

        try {
            const productList = (await fetchProducts(1, 1000, 'id:asc', null)) || [];
            setProducts(productList);
            if (productList.length > 0) {
                setProductId(productList[0].id as number);
                setProductCode(productList[0].code as string);
            }

            setStatusId(Number(params.status) || '');
            const regionList = await fetchRegionsDict();
            setRegions(regionList);
            if (regionList.length === 1) {
                const [currentRegion] = regionList;
                const code = productList.length > 0 ? productList[0].code : '';
                setRegionId(currentRegion.id);
                setRegionCode(currentRegion.code);
                await getBranchesByRegionCode(currentRegion.code, code);
                fetchActiveCouriers(regionList[0].code || null).then((res) => {
                    setActiveCouriers(res.content);
                });
            }

            setEmcTypes(await fetchEmcTypes());

            if (!params.status) {
                setStatuses((await fetchAccessedStatuses()) as StatusCommonDto[]);
            }
        } catch (e) {
            enqueueSnackbar(`Ошибка при загрузке справочников: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        }
    }, [products, fetchProducts, params.status, getBranchesByRegionCode, enqueueSnackbar]);

    const handleSearchKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            if (isReserved) {
                handleReserveSearch();
            } else {
                handleSearch();
            }
        }
    };

    const handleSelectedOpen = () => {
        setSelectedOpen(true);
    };

    const handleSelectedClose = () => {
        setSelectedOpen(false);
    };

    const handleOrderDetailOpen = (id: number) => {
        setOrderId(id);
        setOrderDetailOpen(true);
    };

    const handleOrderDetailClose = () => {
        setOrderDetailOpen(false);
        if (isOrderChanged) getAll();
    };

    const handleDeleteAll = () => {
        setSelected([]);
        setSelectedItems([]);
        setTotalSum(0);
    };

    useEffect(() => {
        getActions();
    }, [getActions]);

    useEffect(() => {
        getDictionaries();
    }, [getDictionaries]);

    useEffect(() => {
        setSingleSelect(getDoubleSelect());
    }, [getDoubleSelect]);

    useEffect(() => {
        setMenuStatusId(params.status);
    }, [params.status]);

    useEffect(() => {
        if (regions.length === 0 || (regions.length === 1 && !regionId)) {
            return;
        }

        if (
            (regions.length === 1 && branches?.length === 0) ||
            (branches?.length === 1 && !branchId)
        ) {
            return;
        }

        if (productId) {
            if (menuStatusId) {
                if (statusId) {
                    if (!isReserved) {
                        getAll();
                    } else {
                        if (courier.current) {
                            getAll();
                        }
                    }
                }
            } else {
                getAll();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productId,
        menuStatusId,
        statusId,
        page,
        perPage,
        regionId,
        branchId,
        regions,
        isReserved,
        branches,
        geocodingStatusId,
        geozoneName
    ]);

    useEffect(() => {
        if (regions.length === 0 || (regions.length === 1 && !regionId)) {
            return;
        }

        if (branches?.length === 0 || (branches?.length === 1 && !branchId)) {
            return;
        }

        if (productId) {
            if (menuStatusId) {
                if (statusId) {
                    if (isReserved) {
                        handleReserveSearch();
                    }
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, menuStatusId, statusId, regionId, regions, branches, branchId]);

    return {
        loading,
        actionLoading,
        items,
        productId,
        productCode,
        products,
        handleProductChange,
        emcTypeId,
        emcTypes,
        handleEmcTypeChange,
        isReserved,
        regionId,
        handleRegionChange,
        regions,
        courier,
        activeCouriers,
        startDate,
        handleStartDateChange,
        untilDate,
        handleUntilDateChange,
        menuStatusId,
        statuses,
        statusId,
        handleStatusChange,
        search,
        handleSearchChange,
        handleSearchKeyDown,
        handleSearch,
        handleReset,
        selected,
        actions,
        handleActionClick,
        singleSelect,
        handleSelectAllClick,
        isSelected,
        handleClick,
        total,
        perPage,
        page,
        setPage,
        setPerPage,
        activeAction,
        commentOpen,
        handleCommentClose,
        courierDialogOpen,
        handleActionCourierSelect,
        handleActionCourierClose,
        selectedItems,
        selectedOpen,
        handleSelectedOpen,
        handleSelectedClose,
        orderId,
        orderDetailOpen,
        handleOrderDetailOpen,
        handleOrderDetailClose,
        handleDeleteAll,
        getAllWithBarcode,
        barCodeLoading,
        courierSelectLoading,
        tabValue,
        handleTabChange,
        courierList,
        loadCouriers,
        courierHasMore,
        handleReserveSearch,
        courierCount,
        getAll,
        setSelected,
        setSelectedItems,
        isCourierCall,
        courierCallOpen,
        handleCourierCallClose,
        handleCourierCallClick,
        isReceived,
        branches,
        handleBranchChange,
        branchId,
        branchCode,
        hasPermissionToEdit:
            roles?.length > 0 && roles?.filter((r) => r === 'ADMIN' || r === 'SUPPORT').length > 0,
        totalSum,
        setTotalSum,
        isGeocoding,
        isGeozoning,
        geocodingStatuses,
        geocodingStatusId,
        handleGeocodingStatusChange,
        setIsOrderChanged,
        geozones,
        geozoneName,
        handleGeozoneChange,
        DROPDOWN_SELECT_ALL,
        NOT_GEOZONE
    };
}

export default useOrderLogic;
